import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { staffLogin, staffVerify2FA, setStaffToken, formatErr } from "@/lib/staffApi";
import { useStaff } from "@/pages/staff/StaffApp";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

export default function StaffLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [twofa, setTwofa] = useState("");
  const [step2fa, setStep2fa] = useState(false);
  const [emailMasked, setEmailMasked] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { refresh } = useStaff();
  const navigate = useNavigate();

  const finish = async (data) => {
    setStaffToken(data.token);
    await refresh();
    if (data.must_change_password) navigate("/staff/cambiar-password");
    else navigate(data.user?.role === "mantenimiento" ? "/staff/admin" : "/staff/bandeja");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setTwofa("");
    setLoading(true);
    try {
      const data = await staffLogin(email.trim(), password);
      if (data.status === "2fa_required") {
        setStep2fa(true);
        setEmailMasked(data.email_masked || "");
        setLoading(false);
        return;
      }
      if (data.status === "2fa_unavailable") {
        setTwofa(data.detail || "2FA no configurado");
        setLoading(false);
        return;
      }
      await finish(data);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await staffVerify2FA(email.trim(), password, code.trim());
      await finish(data);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-orange-500 font-black text-2xl tracking-tight">AUTOFINANZA</div>
          <div className="text-sm text-neutral-500 mt-1">Acceso interno · Staff</div>
        </div>

        {!step2fa && (
          <form onSubmit={submit} data-testid="staff-login-form"
            className="bg-[#111] border border-neutral-800 rounded-2xl p-7 space-y-5">
            <div>
              <label className="text-xs text-neutral-400">Correo corporativo</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="staff-login-email"
                className="mt-1 w-full bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2.5 text-sm focus:border-orange-500 outline-none" />
            </div>
            <div>
              <label className="text-xs text-neutral-400">Contraseña</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="staff-login-password"
                className="mt-1 w-full bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2.5 text-sm focus:border-orange-500 outline-none" />
            </div>
            {err && <div className="text-sm text-red-400" data-testid="staff-login-error">{err}</div>}
            {twofa && (
              <div className="flex items-start gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3"
                data-testid="staff-2fa-blocked">
                <ShieldAlert size={18} className="mt-0.5 shrink-0" />
                <span>{twofa}</span>
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="staff-login-submit"
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />} Ingresar
            </button>
          </form>
        )}

        {step2fa && (
          <form onSubmit={submit2fa} data-testid="staff-2fa-form"
            className="bg-[#111] border border-neutral-800 rounded-2xl p-7 space-y-5">
            <div className="flex items-center gap-2 text-orange-400 text-sm">
              <ShieldCheck size={18} /> Verificación en dos pasos
            </div>
            <p className="text-sm text-neutral-400">
              Enviamos un código a tu correo{emailMasked ? ` ${emailMasked}` : ""}. Ingrésalo para completar tu acceso.
            </p>
            <input inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
              data-testid="staff-2fa-code" placeholder="Código de verificación"
              className="w-full bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2.5 text-center tracking-[0.4em] text-lg focus:border-orange-500 outline-none" />
            {err && <div className="text-sm text-red-400" data-testid="staff-2fa-error">{err}</div>}
            <button type="submit" disabled={loading} data-testid="staff-2fa-submit"
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />} Verificar y entrar
            </button>
            <button type="button" onClick={() => { setStep2fa(false); setCode(""); setErr(""); }}
              data-testid="staff-2fa-back" className="w-full text-neutral-500 text-sm hover:text-neutral-300">
              Volver
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
